import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, Subject, catchError, map, skipWhile, switchMap, take} from 'rxjs';
import {AutoDestroy, BML, BaseHttpService} from '@ox/utils';
import {Asset, AssetType, Assets, BrandAsset, Brands, GeneralAsset, PageAssetGroup, PlanAsset, UIFeatureFlag, BML as iBML} from '@ox/typings';
import {apiUrlMap} from '@ox/config';
import {Store} from '@ngrx/store';
import {selectFeatureFlag} from '@ox/data';
import {first} from 'lodash';

@Injectable()
export class AssetsService {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  private _assets = new BehaviorSubject<Assets>({} as Assets);
  private cache = new Map<string, Assets>;
  public brand = this.bml.brand;

  get assets() {
    return this._assets.getValue();
  }

  set assets(assets: Assets) {
    this._assets.next(assets);
  }

  constructor(
    private http: BaseHttpService,
    @Inject(BML) private bml: iBML,
    private store: Store
  ) {}

  getAssets() {
    return this.getFlagState().pipe(
      switchMap((assetsFlag) => {
        return this.http.get(apiUrlMap.content.assets(this.bml.market, this.bml.lang, assetsFlag))
          .pipe(
            take(1),
            skipWhile((x) => !x),
            map((response) => {
              if (response) {
                this.assets = response.data as Assets;
                const currentAssets = response.data as Assets;
                this.cache.set(this.brand, currentAssets);
                return this.assets;
              } else {
                this.cache.delete(this.brand);
                throw new Error('No Assets');
              }
            }),
            catchError(() => {
              this.cache.delete(this.brand);
              throw new Error('Asset Retrieval failed');
            })
          );
      })
    );
  }

  getFlagState(){
    return this.store.select(selectFeatureFlag({id: UIFeatureFlag.ASSETS, group: 'UI'}))
      .pipe(
        take(1),
        map(assetFlag => assetFlag || '')
      );
  }

  getSpecificPlanAssetByType(planName: string, assetId: string, assetType: AssetType) {
    return this.getPlanAssetsByName(planName)?.find((planAsset: PlanAsset) => planAsset.id === assetId)?.assets
      .find((asset: any) => asset.type === assetType);
  }

  getPlanAssetsByName(name: string) {
    return this.assets?.plans.find((plan) => plan.name === name)?.planAssets;
  }

  getSpecificFeatureAssetByType(featureCode: string, assetId: string, assetType: AssetType, brand: Brands) {
    const featureAsset = this.getFeatureAssetGroup(featureCode)?.featureAssets?.find((asset) => asset.id === assetId);
    // check for branded asset, if not found then search for unbranded asset
    let asset = featureAsset?.brands
      .find((featureBrand) => featureBrand.brand.toLowerCase() === brand)?.assets
      .find((asset) => asset.type === assetType);
    if (!asset)
      asset = featureAsset?.brands
        .find((featureBrand) => featureBrand.brand === 'ALL')?.assets
        .find((asset) => asset.type === assetType);
    return asset;
  }

  getFeatureAssetGroup(code: string) {
    return this.assets?.features.find((feature) => feature.code === code);
  }
  
  getPageAssets(id: string, assetsType: string, pageName: string, brand: string){
    const {generalAssets} = this.assets.pages.find((page: PageAssetGroup) => page.pageName === pageName) || {};
    const {brands} = generalAssets?.find((asset: GeneralAsset) => asset.id === id) || {};
    const {assets} = brands?.find((asset: BrandAsset) => asset.brand.toLowerCase() === brand.toLowerCase()) || {};
    const imageAssets = assets?.filter((asset: Asset) => asset.type === assetsType) || [];
    if (!imageAssets || imageAssets.length === 0) {
      return;
    }
    return first(imageAssets);
  }
}
